"use client";

import React from "react";
import { ShieldCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const milestones = [{
  year: "1924",
  title: "The First Hearth",
  description: "Our great-grandfather fired up the first brick oven in a small village, using wild yeast and local grains to feed the community."
}, {
  year: "1958",
  title: "Generational Secrets",
  description: "The family recipes were passed down, refining the art of the long-fermentation sourdough that would eventually become our signature loaf."
}, {
  year: "1992",
  title: "Artisan Revival",
  description: "We opened our first boutique storefront, bringing time-honored European techniques to a new generation of bread enthusiasts."
}, {
  year: "2018",
  title: "CRUST is Born",
  description: "Rebranding as CRUST, we focused on the perfect marriage of heritage methods and modern, premium ingredients for the ultimate bake."
}, {
  year: "2024",
  title: "A Century of Flour",
  description: "Celebrating 100 years of baking excellence, continuing to deliver fresh, comforting nostalgia straight from our ovens to your door."
}];
function TimelineItem({
  item,
  i,
  isLast
}: {
  item: (typeof milestones)[0];
  i: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div data-section-id="223177" data-index={i} ref={ref} className={`flex group transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className="w-20 sm:w-32 shrink-0 text-right pt-1.5 pr-4 sm:pr-6">
        <span className="font-default text-xl sm:text-2xl font-bold text-primary tracking-tight">
          {item.year}
        </span>
      </div>

      {}
      <div className="relative flex flex-col items-center shrink-0">
        <div className="size-4 rounded-full bg-primary ring-4 ring-background z-10 mt-2 shadow-[0_0_15px_rgba(150,90,56,0.5)] transition-transform duration-300 group-hover:scale-125" />
        {!isLast && <div className="w-px h-full bg-primary/30 absolute top-6 bottom-0" />}
      </div>

      {}
      <div className="flex-1 pl-6 sm:pl-8 pb-12 sm:pb-16">
        <Card className="bg-card/40 backdrop-blur-md border-border/50 shadow-md hover:shadow-lg transition-shadow duration-300 hover:bg-card/60 rounded-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg sm:text-xl font-default text-foreground">
              {item.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription className="text-base text-muted-foreground leading-relaxed font-sans">
              {item.description}
            </CardDescription>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function MilestonesOfIntegrity() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="our-heritage" className="dark bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 overflow-hidden relative">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-4xl mx-auto relative z-10">
        <div ref={headerRef} className={`text-center mb-20 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-2xl mb-6 ring-1 ring-primary/20">
            <ShieldCheck className="size-8 text-primary" />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
            Our Heritage
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto font-sans">
            A century-old legacy of artisan baking, built on time-honored techniques and a relentless commitment to the perfect golden crust.
          </p>
        </div>

        <div className="relative">
          {milestones.map((item, i) => <TimelineItem key={i} item={item} i={i} isLast={i === milestones.length - 1} />)}
        </div>
      </div>
    </section>;
}